import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiTrash2 } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import SsdiLinkBankerAgency from "./SsdiLinkBankerAgency";
import "../../../styles/users.css";

function dash(value) {
  const text = String(value || "").trim();
  return text || "—";
}

function bankStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending_payment") return "Pending";
  if (s === "active") return "Active";
  if (s === "expired") return "Expired";
  if (s === "inactive") return "Blocked";
  return dash(status);
}

function ReadField({ id, label, value }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <input id={id} value={dash(value)} readOnly className="input-readonly" />
    </div>
  );
}

export default function SsdiBankDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [bank, setBank] = useState(null);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteBankPersonId, setInviteBankPersonId] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteAgencyName, setInviteAgencyName] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteSaving, setInviteSaving] = useState(false);
  const [deletingBank, setDeletingBank] = useState(false);

  useEffect(() => {
    bankService
      .ssdiGetBank(id)
      .then((bankRes) => {
        const data = bankRes?.data?.data;
        setBank(data?.bank);
        setPersons(data?.persons || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleDeleteBank = async () => {
    if (!bank || deletingBank) return;
    const label = bank.bankName || bank.bankCode || "this bank";
    if (
      !window.confirm(
        `Delete ${label}? This permanently removes the bank account, users, uploads, and related data. This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingBank(true);
    try {
      await bankService.ssdiDeleteBank(id);
      navigate("/ssdi/banks");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete bank");
      setDeletingBank(false);
    }
  };

  const handleCreateInvite = async () => {
    setInviteError("");
    if (!inviteBankPersonId) {
      setInviteError("Select a banker");
      return;
    }
    setInviteSaving(true);
    try {
      const res = await bankService.ssdiCreateInvite({
        bankPersonId: inviteBankPersonId,
        agencyEmail: inviteEmail,
        agencyName: inviteAgencyName,
      });
      setInviteUrl(res?.data?.data?.inviteUrl || "");
    } catch (err) {
      setInviteError(err?.response?.data?.message || "Failed to create invite");
    } finally {
      setInviteSaving(false);
    }
  };

  const handleCopyInvite = () => {
    if (inviteUrl) navigator.clipboard.writeText(inviteUrl);
  };

  if (loading) {
    return (
      <div className="page create-company-page ssdi-bank-details-page">
        <p className="muted">Loading...</p>
      </div>
    );
  }

  if (!bank) {
    return (
      <div className="page create-company-page ssdi-bank-details-page">
        <p>Bank not found.</p>
      </div>
    );
  }

  const bankAdminId = bank.adminUserId?._id || bank.adminUserId;
  const bankerOptions = bankAdminId
    ? [
        {
          _id: String(bankAdminId),
          name: bank.bankName || "Bank",
          email: bank.email || bank.adminUserId?.email || "",
        },
      ]
    : [];

  return (
    <div className="page create-company-page repo-form-page ssdi-bank-details-page">
      <div className="create-company-header ssdi-bank-details-header">
        <div className="ssdi-bank-details-header__text">
          <h2>{bank.bankName}</h2>
          <p className="muted ssdi-bank-details-subtitle">
            <span className="ssdi-bank-details-code">{bank.bankCode}</span>
            <span className="ssdi-bank-details-sep" aria-hidden>
              ·
            </span>
            <span className="ssdi-bank-details-email">{bank.email}</span>
          </p>
        </div>
        <div className="company-actions-cell">
          <button
            type="button"
            className="secondary-page-btn"
            onClick={() => navigate("/ssdi/banks")}
          >
            Back to Banks
          </button>
          <button
            type="button"
            className="company-btn company-btn--delete"
            disabled={deletingBank}
            onClick={handleDeleteBank}
          >
            <FiTrash2 aria-hidden />
            <span>{deletingBank ? "Deleting…" : "Delete Bank"}</span>
          </button>
        </div>
      </div>

      <div className="ssdi-bank-details-stack">
        <section className="create-company-card ssdi-bank-details-card">
          <header className="create-company-section__head">
            <h3>Bank details</h3>
          </header>
          <div className="form-grid two-column">
            <ReadField id="ssdi-bank-name" label="Bank Name" value={bank.bankName} />
            <ReadField id="ssdi-bank-code" label="Code" value={bank.bankCode} />
            <ReadField
              id="ssdi-bank-status"
              label="Status"
              value={bankStatusLabel(bank.status)}
            />
            <ReadField id="ssdi-bank-email" label="Email" value={bank.email} />
            <ReadField id="ssdi-bank-phone" label="Phone" value={bank.phone} />
            <ReadField id="ssdi-bank-gst" label="GST Number" value={bank.gstNumber} />
            <ReadField id="ssdi-bank-pan" label="PAN Number" value={bank.panNumber} />
            <ReadField id="ssdi-bank-branch" label="Branch" value={bank.branchName} />
            <ReadField id="ssdi-bank-city" label="City" value={bank.city} />
            <ReadField id="ssdi-bank-state" label="State" value={bank.state} />
            <div className="form-group full-width">
              <label htmlFor="ssdi-bank-address">Address</label>
              <input
                id="ssdi-bank-address"
                value={dash(bank.address)}
                readOnly
                className="input-readonly"
              />
            </div>
          </div>

          <header className="create-company-section__head ssdi-bank-details-admin-head">
            <h3>Bank admin account</h3>
          </header>
          <div className="form-grid two-column">
            <ReadField
              id="ssdi-admin-name"
              label="Admin Name"
              value={bank.adminUserId?.name}
            />
            <ReadField
              id="ssdi-admin-email"
              label="Admin Email"
              value={bank.adminUserId?.email || bank.email}
            />
            <ReadField
              id="ssdi-admin-branch"
              label="Admin branch"
              value={bank.adminUserId?.branchName}
            />
            <ReadField
              id="ssdi-admin-emp"
              label="Admin employee number"
              value={bank.adminUserId?.employeeNumber}
            />
          </div>
        </section>

        <SsdiLinkBankerAgency />

        <section className="create-company-card ssdi-bank-details-card">
          <header className="create-company-section__head">
            <h3>Persons in this bank</h3>
          </header>
          <div className="company-table-wrap ssdi-excel-sheet table-scroll">
            <table className="users-table excel-grid-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Branch</th>
                  <th>Emp #</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {persons.length === 0 ? (
                  <tr>
                    <td colSpan={6}>No persons added by bank admin yet.</td>
                  </tr>
                ) : (
                  persons.map((p) => (
                    <tr key={p._id}>
                      <td>{dash(p.name)}</td>
                      <td>{dash(p.email)}</td>
                      <td>{dash(p.phone)}</td>
                      <td>{dash(p.branchName)}</td>
                      <td>{dash(p.employeeNumber)}</td>
                      <td>
                        <span
                          className={`company-status company-status--${
                            p.isActive ? "active" : "inactive"
                          }`}
                        >
                          {p.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="create-company-card ssdi-bank-details-card ssdi-bank-details-card--last">
          <header className="create-company-section__head">
            <h3>Create Invite (Agency not yet registered)</h3>
          </header>

          <div className="form-grid two-column ssdi-bank-details-inline-form">
            <div className="form-group">
              <label htmlFor="inviteBankPerson">Banker</label>
              <select
                id="inviteBankPerson"
                value={inviteBankPersonId}
                onChange={(e) => setInviteBankPersonId(e.target.value)}
              >
                <option value="">Select banker</option>
                {bankerOptions.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}
                    {b.email ? ` — ${b.email}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="inviteEmail">Agency Email (optional)</label>
              <input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="agency@email.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="inviteAgencyName">Agency Name (optional)</label>
              <input
                id="inviteAgencyName"
                value={inviteAgencyName}
                onChange={(e) => setInviteAgencyName(e.target.value)}
                placeholder="Agency name"
              />
            </div>
            <div className="form-group full-width ssdi-bank-details-form-submit">
              <button
                type="button"
                className="primary-page-btn"
                onClick={handleCreateInvite}
                disabled={inviteSaving}
              >
                {inviteSaving ? "Creating..." : "Generate Invite Link"}
              </button>
            </div>
          </div>
          {inviteError && (
            <p className="error-text ssdi-bank-details-error">{inviteError}</p>
          )}
          {inviteUrl && (
            <div className="ssdi-bank-details-invite-box">
              <p className="ssdi-bank-details-invite-box__title">
                Invite link generated
              </p>
              <code className="ssdi-bank-details-invite-box__url">{inviteUrl}</code>
              <button
                type="button"
                className="secondary-page-btn"
                onClick={handleCopyInvite}
              >
                Copy Link
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
